import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { useCall } from '@/contexts/CallContext';
import { API_CONFIG, ApiError } from '@/lib/api';
import { showApiError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  Send,
  Smile,
  Paperclip,
  ArrowLeft,
  AtSign,
  Edit2,
  Trash2,
  Check,
  CheckCheck,
  X,
  Reply,
  Copy,
  ChevronDown,
  MessageCircle,
  Phone,
  Video,
  Eraser,
  Star,
  StarOff,
  Forward,
} from 'lucide-react';
import { format, isValid } from 'date-fns';
import { formatDateSeparator, parseUTCDate } from '@/utils/dateUtils';
import { useStarredMessages } from '@/hooks/useStarredMessages';
import { ForwardMessageDialog } from './ForwardMessageDialog';

// Extract a parseable date string from a message object.
// Falls back to the sk field (format: "{isoDate}#{id}") if created_at is not a string.
function extractDateStr(msg: any): string | null {
  if (typeof msg.created_at === 'string' && msg.created_at.length > 0) return msg.created_at;
  if (typeof msg.updated_at === 'string' && msg.updated_at.length > 0) return msg.updated_at;
  if (typeof msg.sk === 'string' && msg.sk.includes('#')) return msg.sk.split('#')[0];
  return null;
}

function safeFormatTime(dateStr: string | undefined | null): string {
  if (!dateStr) return format(new Date(), 'h:mm a');
  const raw = typeof dateStr === 'string' ? dateStr : String(dateStr);
  const d = parseUTCDate(raw);
  return d ? format(d, 'h:mm a') : format(new Date(), 'h:mm a');
}

function wasEdited(createdAt: string | undefined | null, updatedAt: string | undefined | null): boolean {
  if (!updatedAt || !createdAt) return false;
  const created = new Date(createdAt).getTime();
  const updated = new Date(updatedAt).getTime();
  if (isNaN(created) || isNaN(updated)) return false;
  return Math.abs(updated - created) > 2000;
}

interface DirectMessageWindowProps {
  recipientId: string;
  recipientName: string;
  onClose: () => void;
  className?: string;
  variant?: 'default' | 'cardTheme';
}

interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: string;
  is_online: boolean;
  last_seen: string;
}

export function DirectMessageWindow({
  recipientId,
  recipientName,
  onClose,
  className,
  variant = 'default',
}: DirectMessageWindowProps) {
  const isCardTheme = variant === 'cardTheme';
  const {
    isConnected,
    messages,
    sendDirectMessage,
    setTyping,
    editMessage,
    deleteMessage,
    clearChat,
    joinRoom,
    currentUser,
    markAsRead,
    markDmMessagesAsRead,
    typingUsers: contextTypingUsers,
    teamMembers
  } = useChat();
  const { initiateCall } = useCall();
  const { toast } = useToast();

  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState<any>(null);
  const [recipient, setRecipient] = useState<User | null>(null);
  const [isLoadingRecipient, setIsLoadingRecipient] = useState(true);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [forwardingMessage, setForwardingMessage] = useState<any>(null);
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const { isStarred, toggleStar } = useStarredMessages(currentUser?.id);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Canonical sorted DM room ID — matches backend sort logic
  const dmRoomId = useMemo(() => {
    if (!currentUser?.id || !recipientId) return '';
    const [id1, id2] = [currentUser.id, recipientId].sort();
    return `dm_${id1}_${id2}`;
  }, [currentUser?.id, recipientId]);

  // Is the recipient currently typing?
  const recipientIsTyping = contextTypingUsers.some(t =>
    t.userId === recipientId && t.roomId === dmRoomId
  );

  // Live online status from teamMembers
  const isRecipientOnline = teamMembers?.find(m => m.id === recipientId)?.is_online ?? false;

  // Filter messages for this DM — accept both sorted and legacy (unsorted) room IDs
  const dmMessages = messages.filter(msg => {
    if (!currentUser?.id || !recipientId) return false;
    const roomId1 = `dm_${currentUser.id}_${recipientId}`;
    const roomId2 = `dm_${recipientId}_${currentUser.id}`;
    return msg.room_id === roomId1 || msg.room_id === roomId2;
  }).filter((msg, index, self) =>
    index === self.findIndex(m => m.id === msg.id)
  ).filter(msg => !showStarredOnly || isStarred(msg.id)).sort((a, b) => {
    const da = parseUTCDate(extractDateStr(a));
    const db = parseUTCDate(extractDateStr(b));
    if (!da && !db) return 0;
    if (!da) return -1;
    if (!db) return 1;
    return da.getTime() - db.getTime();
  });

  // Set recipient data from props immediately
  useEffect(() => {
    if (recipientId && recipientName) {
      setRecipient({
        id: recipientId,
        full_name: recipientName,
        email: '', // We don't have email from props
        avatar_url: undefined,
        role: '',
        is_online: false,
        last_seen: new Date().toISOString(),
      });
      setIsLoadingRecipient(false);
    } else {
      setIsLoadingRecipient(true);
    }
  }, [recipientId, recipientName]);

  // Also set recipient data immediately on mount if props are available
  useEffect(() => {
    if (recipientId && recipientName && !recipient) {
      setRecipient({
        id: recipientId,
        full_name: recipientName,
        email: '',
        avatar_url: undefined,
        role: '',
        is_online: false,
        last_seen: new Date().toISOString(),
      });
      setIsLoadingRecipient(false);
    }
  }, [recipientId, recipientName, recipient]);

  // Debug logging
  useEffect(() => {
  }, [recipientId, recipientName, recipient, isLoadingRecipient, isConnected, currentUser]);

  // Join via REST (history + currentRoom). Socket emit inside joinRoom is optional.
  useEffect(() => {
    if (dmRoomId) {
      joinRoom(dmRoomId);
    }
  }, [dmRoomId, joinRoom]);

  // Clear unread badge and mark messages as read when conversation is opened
  useEffect(() => {
    if (recipientId) {
      markAsRead(recipientId);
    }
    if (dmRoomId && isConnected) {
      markDmMessagesAsRead(dmRoomId);
    }
  }, [recipientId, dmRoomId, isConnected, markAsRead, markDmMessagesAsRead]);

  // Mark new incoming messages as read while this window is open
  const dmMessageCount = dmMessages.length;
  useEffect(() => {
    if (dmRoomId && isConnected && dmMessageCount > 0) {
      markDmMessagesAsRead(dmRoomId);
    }
  }, [dmMessageCount, dmRoomId, isConnected, markDmMessagesAsRead]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [dmMessages]);

  // Handle typing indicator — emit to the sorted DM room explicitly
  const handleTyping = (value: string) => {
    setMessage(value);

    if (!isTyping && value.trim()) {
      setIsTyping(true);
      setTyping(true, dmRoomId);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      setTyping(false, dmRoomId);
    }, 1000);
  };

  const commonEmojis = useMemo(() => ([
    '😀', '😃', '😄', '😁', '😊', '😍', '😘', '🤔',
    '😎', '😭', '😤', '😡', '👍', '👎', '🙏', '👏',
    '🔥', '💯', '🎉', '❤️', '✅', '❌', '⭐', '⚡'
  ]), []);

  const insertAtCursor = (text: string) => {
    const input = inputRef.current;
    if (!input) {
      setMessage((prev) => prev + text);
      return;
    }

    const start = input.selectionStart ?? message.length;
    const end = input.selectionEnd ?? message.length;
    const before = message.slice(0, start);
    const after = message.slice(end);
    const next = before + text + after;
    setMessage(next);

    setTimeout(() => {
      input.focus();
      const pos = start + text.length;
      input.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertEmoji = (emoji: string) => {
    insertAtCursor(emoji);
    setEmojiPickerOpen(false);
  };

  const uploadAttachment = async (file: File) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Not authenticated');

    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_CONFIG.BASE_URL}/chat/upload-attachment`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.success === false) {
      throw ApiError.fromResult(json, 'Failed to upload attachment');
    }

    return json?.data || json;
  };

  const handleConfirmClearChat = async () => {
    if (!dmRoomId) return;
    setIsClearing(true);
    const success = await clearChat(dmRoomId);
    setIsClearing(false);
    setIsClearDialogOpen(false);
    if (success) {
      toast({ variant: "success", title: 'Chat cleared', description: 'All messages have been removed.' });
    } else {
      toast({ title: 'Error', description: 'Failed to clear chat. Please try again.', variant: 'destructive' });
    }
  };

  const handlePickFile = () => {
    if (!isConnected || isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isConnected || !recipientId) return;

    setIsUploading(true);
    try {
      const uploaded = await uploadAttachment(file);
      const isImage = file.type.startsWith('image/');
      const placeholder = isImage ? `🖼️ ${file.name}` : `📎 ${file.name}`;

      sendDirectMessage(recipientId, placeholder, isImage ? 'image' : 'file', {
        url: uploaded.url,
        key: uploaded.key,
        originalName: uploaded.originalName || file.name,
        mimeType: uploaded.mimeType || file.type,
        size: uploaded.size || file.size,
      });
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Upload failed', description: 'Failed to upload attachment' } });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const parseMetadata = (raw: any) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return null;
  };

  const renderMessageTextOrAttachment = (msg: any, isCurrentUser: boolean) => {
    const meta = parseMetadata(msg.metadata) || {};
    const time = safeFormatTime(extractDateStr(msg));
    const edited = wasEdited(msg.created_at, msg.updated_at);
    const msgStatus = msg.status || 'sent';
    const forwardedFrom = meta?.forwarded?.originalSenderName;
    const timeStamp = (
      <span className={cn(
        'inline-flex items-center gap-1 ml-2 text-xs whitespace-nowrap flex-shrink-0',
        isCardTheme && isCurrentUser ? 'text-white/80' : 'text-gray-600 opacity-80',
      )}>
        {edited && <span>(edited)</span>}
        {time}
        {isCurrentUser && (
          msgStatus === 'read'
            ? <CheckCheck className="h-3 w-3 text-blue-500" />
            : msgStatus === 'delivered'
              ? <CheckCheck className="h-3 w-3 text-gray-400" />
              : <Check className="h-3 w-3 text-gray-400" />
        )}
      </span>
    );
    const forwardedLabel = forwardedFrom ? (
      <div className="flex items-center gap-1 text-[11px] italic text-gray-500 mb-1">
        <Forward className="h-3 w-3" />
        <span>Forwarded{forwardedFrom ? ` from ${forwardedFrom}` : ''}</span>
      </div>
    ) : null;

    // Helper to resolve email/name to display name
    const resolveDisplayName = (nameOrEmail: string, senderId?: string) => {
      if (senderId) {
        const member = teamMembers?.find(m => m.id === senderId);
        if (member?.full_name) return member.full_name;
      }
      if (nameOrEmail && nameOrEmail.includes('@')) {
        const member = teamMembers?.find(m => m.email === nameOrEmail);
        if (member?.full_name) return member.full_name;
      }
      return nameOrEmail || 'Unknown';
    };

    let body: React.ReactNode;

    if (msg.message_type === 'image' && meta.url) {
      body = (
        <div>
          <img
            src={meta.url}
            alt={meta.originalName || 'image'}
            className="max-w-[260px] max-h-[260px] rounded-md object-cover"
            loading="lazy"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs opacity-80 truncate">{meta.originalName}</span>
            {timeStamp}
          </div>
        </div>
      );
    } else if ((msg.message_type === 'file' || msg.message_type === 'video' || msg.message_type === 'audio') && meta.url) {
      body = (
        <div>
          {msg.message_type === 'video' ? (
            <video src={meta.url} controls className="max-w-[260px] max-h-[260px] rounded-md" />
          ) : msg.message_type === 'audio' ? (
            <audio src={meta.url} controls className="max-w-[260px]" />
          ) : (
            <a
              href={meta.url}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 text-sm break-all"
            >
              {meta.originalName || msg.content}
            </a>
          )}
          <div className="flex items-center justify-end mt-1">
            {timeStamp}
          </div>
        </div>
      );
    } else if (meta?.replyTo) {
      // WhatsApp-style reply rendering
      const replyInfo = meta.replyTo;
      const repliedToName = resolveDisplayName(replyInfo.senderName, replyInfo.senderId);
      body = (
        <div className="space-y-2">
          <div
            className="rounded-lg bg-gray-50 border-l-[3px] border-emerald-500 overflow-hidden cursor-pointer hover:bg-gray-100 transition-colors"
          >
            <div className="px-3 py-2 min-w-0">
              <div className="text-[11px] font-semibold text-emerald-600 truncate">{repliedToName}</div>
              <div className="text-xs text-gray-500 truncate mt-0.5">{replyInfo.content || '...'}</div>
            </div>
          </div>
          <div className="inline">
            <span>{msg.content}</span>
            {timeStamp}
          </div>
        </div>
      );
    } else if (msg.content.startsWith('Replying to')) {
      // Legacy "Replying to Name: message" format
      const firstColon = msg.content.indexOf(':');
      const rawName = msg.content.substring(0, firstColon).replace('Replying to ', '');
      const replyText = msg.content.substring(firstColon + 1).trim();
      const repliedToName = resolveDisplayName(rawName);
      body = (
        <div className="space-y-2">
          <div className="rounded-lg bg-gray-50 border-l-[3px] border-emerald-500 overflow-hidden">
            <div className="px-3 py-2 min-w-0">
              <div className="text-[11px] font-semibold text-emerald-600 truncate">{repliedToName}</div>
              <div className="text-xs text-gray-500 truncate mt-0.5">{replyText}</div>
            </div>
          </div>
          <div className="inline">
            <span>{replyText}</span>
            {timeStamp}
          </div>
        </div>
      );
    } else {
      body = (
        <div className="flex items-end justify-between gap-2 flex-nowrap">
          <span className="whitespace-pre-wrap break-words flex-1 min-w-0">{msg.content}</span>
          {timeStamp}
        </div>
      );
    }

    return <>{forwardedLabel}{body}</>;
  };

  // Copy message text to clipboard
  const handleCopyMessage = (msg: any) => {
    const meta = parseMetadata(msg.metadata) || {};
    const textToCopy = meta.url || msg.content;
    navigator.clipboard.writeText(textToCopy).then(() => {
      toast({ variant: "success", title: 'Copied to clipboard' });
    });
    setContextMenu(null);
  };

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; msg: any } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Adjust context menu position if it overflows viewport
  useEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return;
    const el = contextMenuRef.current;
    const rect = el.getBoundingClientRect();
    let { x, y } = contextMenu;
    let changed = false;

    if (rect.bottom > window.innerHeight - 10) {
      y = contextMenu.y - rect.height - 5;
      changed = true;
    }
    if (rect.right > window.innerWidth - 10) {
      x = window.innerWidth - rect.width - 10;
      changed = true;
    }
    if (x < 10) {
      x = 10;
      changed = true;
    }
    if (changed) {
      setContextMenu(prev => prev ? { ...prev, x, y } : null);
    }
  }, [contextMenu]);

  // Close context menu on click outside or scroll
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  const handleSendMessage = () => {
    if (!message.trim() || !recipientId) return;
    if (editingMessageId) {
      handleSaveEdit();
      return;
    }
    const content = message.trim();
    const replyMeta = replyingToMessage
      ? {
          replyTo: {
            id: replyingToMessage.id,
            senderId: replyingToMessage.sender_id,
            senderName: replyingToMessage.sender?.full_name || replyingToMessage.sender?.email || 'Unknown',
            content: replyingToMessage.content?.substring(0, 200) || '',
          },
        }
      : undefined;
    setMessage('');
    setReplyingToMessage(null);
    setIsTyping(false);
    setTyping(false, dmRoomId);
    void Promise.resolve(
      replyMeta
        ? sendDirectMessage(recipientId, content, 'text', replyMeta)
        : sendDirectMessage(recipientId, content),
    ).then((ok) => {
      if (ok === false) {
        setMessage(content);
        toast({
          title: 'Message not sent',
          description: 'Could not send this message. Try again.',
          variant: 'destructive',
        });
      }
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && editingMessageId) {
      e.preventDefault();
      handleCancelEdit();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleEditMessage = (messageId: string, currentContent: string) => {
    setEditingMessageId(messageId);
    setEditContent(currentContent);
    setMessage(currentContent);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSaveEdit = () => {
    if (editingMessageId && message.trim()) {
      editMessage(editingMessageId, message.trim());
      setEditingMessageId(null);
      setEditContent('');
      setMessage('');
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
    setMessage('');
  };

  const handleDeleteMessage = (messageId: string) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMessage(messageId);
    }
  };

  const handleReplyToMessage = (message: any) => {
    setReplyingToMessage(message);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCancelReply = () => {
    setReplyingToMessage(null);
  };

  const isCurrentUserMessage = (senderId: string) => {
    return currentUser?.id === senderId;
  };

  const getSenderName = (msg: any) => {
    if (isCurrentUserMessage(msg.sender_id)) {
      return currentUser?.full_name || currentUser?.email || 'You';
    }
    return recipient?.full_name || msg.sender?.full_name || msg.sender?.email || 'Unknown';
  };

  const getSenderAvatar = (msg: any) => {
    if (isCurrentUserMessage(msg.sender_id)) {
      return currentUser?.avatar_url || msg.sender?.avatar_url;
    }
    return recipient?.avatar_url || msg.sender?.avatar_url;
  };

  return (
    <div className={`flex flex-col h-full min-h-0 bg-background ${className}`} style={{ height: '100%' }}>
      {/* Header */}
      <div className={cn(
        'flex items-center justify-between sticky top-0 z-10 border-b',
        isCardTheme
          ? 'border-[#EAECF0] bg-white px-4 py-3'
          : 'px-6 py-4 bg-white/80 backdrop-blur-md shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]',
      )}>
        <div className="flex items-center gap-3">
          {isCardTheme ? null : (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-9 w-9 hover:bg-black/5 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          )}
          <div className="relative">
            <Avatar className={cn('h-10 w-10', isCardTheme ? '' : 'ring-2 ring-white shadow-sm')}>
              <AvatarImage src={recipient?.avatar_url} className="object-cover" />
              <AvatarFallback
                className={
                  isCardTheme
                    ? 'bg-[#F2F4F7] text-[11px] font-semibold text-[#475467]'
                    : 'bg-gradient-to-br from-sky-500 to-indigo-600 text-white font-medium'
                }
              >
                {isLoadingRecipient ? '...' : getInitials(recipient?.full_name || recipientName)}
              </AvatarFallback>
            </Avatar>
            <span className={`absolute -bottom-0.5 -right-0.5 w-[14px] h-[14px] rounded-full border-[2.5px] border-white shadow-sm ${isRecipientOnline ? 'bg-emerald-500' : 'bg-gray-300'}`} />
          </div>
          <div className="flex flex-col">
            <h3 className="font-bold text-gray-900 text-[1.05rem] tracking-tight">
              {isLoadingRecipient ? 'Loading...' : (recipient?.full_name || recipientName)}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`text-xs font-medium ${isRecipientOnline ? 'text-emerald-600' : 'text-gray-500'}`}>
                {isRecipientOnline ? 'Active Now' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant={showStarredOnly ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            title={showStarredOnly ? 'Show all messages' : 'Show starred messages'}
            onClick={() => setShowStarredOnly((v) => !v)}
            data-testid="dm-starred-toggle"
          >
            <Star className={`h-4 w-4 ${showStarredOnly ? 'fill-yellow-400 text-yellow-500' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Audio call"
            disabled={!isConnected || !isRecipientOnline}
            onClick={() => initiateCall({ targetUserId: recipientId, targetUserName: recipientName, callType: 'audio', isGroupCall: false })}
          >
            <Phone className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Video call"
            disabled={!isConnected || !isRecipientOnline}
            onClick={() => initiateCall({ targetUserId: recipientId, targetUserName: recipientName, callType: 'video', isGroupCall: false })}
          >
            <Video className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Clear chat"
            onClick={() => setIsClearDialogOpen(true)}
          >
            <Eraser className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 p-2 sm:p-4 overflow-y-auto bg-[#F5F7F9]">
        <div className="space-y-3">
          {dmMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-foreground">Start your conversation</h4>
                <p className="text-sm text-muted-foreground">
                  Send a message to {recipient?.full_name || recipientName} to begin chatting
                </p>
              </div>
            </div>
          ) : (
            (() => {
              let lastDateStr = '';
              return dmMessages.map((msg) => {
              const isCurrentUser = isCurrentUserMessage(msg.sender_id);
              const isEditing = editingMessageId === msg.id;

              // Date separator logic
              let dateSeparator = null;
              const rawDate = extractDateStr(msg);
              if (rawDate) {
                const msgDate = parseUTCDate(rawDate);
                const dateStr = msgDate ? format(msgDate, 'yyyy-MM-dd') : '';
                if (dateStr && dateStr !== lastDateStr) {
                  lastDateStr = dateStr;
                  const label = formatDateSeparator(rawDate);
                  dateSeparator = (
                    <div className="flex justify-center my-3" key={`date-${dateStr}`}>
                      <span className="bg-[#e1f2fb] text-[#54656f] text-xs font-medium px-3 py-1 rounded-lg shadow-sm">
                        {label}
                      </span>
                    </div>
                  );
                }
              }

              return (
                <React.Fragment key={msg.id}>
                  {dateSeparator}
                <div
                  className={`flex gap-2 max-w-[80%] ${isCurrentUser ? 'ml-auto flex-row-reverse' : 'mr-auto'}`}
                >
                  {!isCurrentUser && (
                    <Avatar className="h-6 w-6 mt-1">
                      <AvatarImage src={getSenderAvatar(msg)} />
                      <AvatarFallback
                        className={
                          isCardTheme
                            ? 'bg-[#F2F4F7] text-[10px] font-semibold text-[#475467]'
                            : 'text-xs'
                        }
                      >
                        {getInitials(getSenderName(msg))}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  <div className="flex flex-col">
                    {!isCurrentUser && (
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-xs text-gray-500">
                          {getSenderName(msg)}
                        </span>
                      </div>
                    )}

                    <div className="relative group">
                      <div
                        className={`px-4 py-3 shadow-sm rounded-2xl break-words w-fit max-w-sm ${isCurrentUser
                          ? isCardTheme
                            ? 'bg-[#0EB64F] text-white rounded-br-md'
                            : 'bg-white border border-gray-200 text-gray-900 rounded-br-md'
                          : 'bg-white text-gray-900 border border-[#EAECF0] rounded-bl-md'
                        }`}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setContextMenu({ x: e.clientX, y: e.clientY, msg });
                        }}
                      >
                        <div className={cn('text-sm leading-relaxed', isCardTheme && isCurrentUser ? 'text-white' : 'text-gray-900')}>
                          {renderMessageTextOrAttachment(msg, isCurrentUser)}
                        </div>
                      </div>

                      {/* Dropdown arrow on hover - inside bubble top-right */}
                      {!isEditing && (
                        <button
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-full p-0.5 bg-black/5 hover:bg-black/10 text-gray-500 z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (contextMenu?.msg?.id === msg.id) {
                              setContextMenu(null);
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = isCurrentUser ? rect.right - 180 : rect.left;
                            setContextMenu({ x, y: rect.bottom + 4, msg });
                          }}
                          data-testid={`dm-msg-menu-${msg.id}`}
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {isStarred(msg.id) && (
                        <span
                          className={`absolute -bottom-1 ${isCurrentUser ? '-left-1' : '-right-1'} bg-white rounded-full p-0.5 shadow-sm z-10`}
                          title="Starred"
                          data-testid={`dm-msg-star-${msg.id}`}
                        >
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-500" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                </React.Fragment>
              );
            });
            })()
          )}

          {/* WhatsApp-style context menu */}
          {contextMenu && (
            <div
              ref={contextMenuRef}
              className="fixed z-50 min-w-[180px] bg-popover border border-border rounded-lg shadow-xl py-1 animate-in fade-in-0 zoom-in-95"
              style={{ top: contextMenu.y, left: contextMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => { handleReplyToMessage(contextMenu.msg); setContextMenu(null); }}
              >
                <Reply className="h-4 w-4 text-muted-foreground" />
                Reply
              </button>
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => handleCopyMessage(contextMenu.msg)}
              >
                <Copy className="h-4 w-4 text-muted-foreground" />
                Copy
              </button>
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => { toggleStar(contextMenu.msg.id); setContextMenu(null); }}
                data-testid="dm-menu-star"
              >
                {isStarred(contextMenu.msg.id) ? (
                  <>
                    <StarOff className="h-4 w-4 text-muted-foreground" />
                    Unstar
                  </>
                ) : (
                  <>
                    <Star className="h-4 w-4 text-muted-foreground" />
                    Star
                  </>
                )}
              </button>
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => { setForwardingMessage(contextMenu.msg); setContextMenu(null); }}
                data-testid="dm-menu-forward"
              >
                <Forward className="h-4 w-4 text-muted-foreground" />
                Forward
              </button>
              {isCurrentUserMessage(contextMenu.msg.sender_id) && (
                <>
                  <div className="border-t border-border my-1" />
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                    onClick={() => { handleEditMessage(contextMenu.msg.id, contextMenu.msg.content); setContextMenu(null); }}
                  >
                    <Edit2 className="h-4 w-4 text-muted-foreground" />
                    Edit
                  </button>
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left text-destructive"
                    onClick={() => { handleDeleteMessage(contextMenu.msg.id); setContextMenu(null); }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </>
              )}
            </div>
          )}

          {/* Typing indicator */}
          {recipientIsTyping && (
            <div className="flex gap-3">
              <div className="h-8 w-8" />
              <div className="text-sm text-muted-foreground italic">
                {recipientName} is typing...
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Reply Indicator */}
      {replyingToMessage && (
        <div className="px-4 py-3 bg-muted border-t">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Reply className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-foreground">
                    {/* Through getSenderName: `sender` is null for a message from
                        a removed teammate, and every OTHER read here already
                        guards for it — this banner was the one straggler. */}
                    Replying to {getSenderName(replyingToMessage)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {safeFormatTime(extractDateStr(replyingToMessage))}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground bg-background rounded-md p-2 border">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-4 bg-primary rounded-full flex-shrink-0"></div>
                    <span className="truncate">
                      {replyingToMessage.content.length > 100
                        ? replyingToMessage.content.substring(0, 100) + '...'
                        : replyingToMessage.content}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelReply}
              className="h-6 w-6 p-0 flex-shrink-0"
              title="Cancel reply"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Indicator */}
      {editingMessageId && (
        <div className="px-4 py-2 bg-muted border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Edit2 className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-foreground">Editing message</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelEdit}
              className="h-6 w-6 p-0 flex-shrink-0"
              title="Cancel edit"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className={cn(isCardTheme ? 'border-t border-[#EAECF0] bg-white px-4 py-3' : 'p-4 border-t')}>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelected}
        />
        {isCardTheme ? (
          <>
            <div className="flex items-center gap-1 rounded-xl border border-[#E4E7EC] bg-[#F9FAFB] px-2 py-1.5">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828] disabled:opacity-100" onClick={handlePickFile} disabled={isUploading} title="Attach file">
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828]" onClick={() => insertAtCursor('@')} title="Mention">
                <AtSign className="h-4 w-4" />
              </Button>
              <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828]" title="Emoji">
                    <Smile className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="start">
                  <div className="grid grid-cols-8 gap-1">
                    {commonEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="h-8 w-8 rounded hover:bg-muted flex items-center justify-center text-lg"
                        onClick={() => insertEmoji(emoji)}
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Input
                ref={inputRef}
                value={message}
                onChange={(e) => handleTyping(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={replyingToMessage ? 'Type your reply...' : `Message ${recipientName}...`}
                className="h-9 flex-1 border-0 bg-transparent shadow-none text-[#101828] placeholder:text-[#98A2B3] focus-visible:ring-0 disabled:cursor-text disabled:opacity-100"
              />
              <Button
                variant="ghost"
                onClick={handleSendMessage}
                disabled={!message.trim() || !recipientId || isUploading}
                size="icon"
                className="h-9 w-9 shrink-0 rounded-lg bg-[#0EB64F] text-white hover:bg-[#0A8A3B] hover:text-white disabled:pointer-events-none disabled:bg-[#0EB64F] disabled:text-white disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={handlePickFile} disabled={!isConnected || isUploading}>
            <Paperclip className="h-4 w-4" />
          </Button>
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={message}
              onChange={(e) => handleTyping(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={replyingToMessage ? "Type your reply..." : "Type a message..."}
              disabled={!isConnected}
              className="pr-10"
            />
            <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8"
                  disabled={!isConnected}
                >
                  <Smile className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="grid grid-cols-8 gap-1">
                  {commonEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="h-8 w-8 rounded hover:bg-muted flex items-center justify-center text-lg"
                      onClick={() => insertEmoji(emoji)}
                      title={emoji}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!message.trim() || !isConnected || !recipientId || isUploading}
            size="icon"
            className="h-10 w-10"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        )}
      </div>
      {/* Clear chat confirmation dialog */}
      <AlertDialog
        open={isClearDialogOpen}
        onOpenChange={(open) => {
          if (!isClearing) setIsClearDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear chat permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently clear your chat with{' '}
              <span className="font-medium text-foreground">
                {recipient?.full_name || recipientName}
              </span>
              .
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmClearChat();
              }}
              disabled={isClearing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isClearing ? 'Clearing...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ForwardMessageDialog
        open={!!forwardingMessage}
        onOpenChange={(o) => !o && setForwardingMessage(null)}
        message={forwardingMessage}
      />
    </div>
  );
}
